import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  Compiler,
  Injector,
  AfterViewInit,
  ComponentRef
} from '@angular/core';
import { BehaviorSubject, firstValueFrom, forkJoin, Observable, Subject, switchMap, map, tap, takeUntil, filter, distinctUntilChanged, startWith, EMPTY } from 'rxjs';
import {
  loadSchedulerClasses,
  isSchedulerEnabled,
  loadSchedulerComponentFactory,
  configureSchedulerComponent,
  updateSchedulerComponent
} from 'src/app/libs/utils/bryntum.helper';
import { environment } from 'src/environments/environment';

// Tipos de Bryntum - se cargan dinámicamente
type StringHelper = any;
type EventModel = any;
type PresetManager = any;
type ResourceModelConfig = any;
type Scheduler = any;

// Variables para almacenar las clases cargadas dinámicamente
let StringHelperClass: any;
let EventModelClass: any;
let PresetManagerClass: any;
let ResourceModelConfigClass: any;
let SchedulerClass: any;
import { EBackendFilterTypes } from 'src/app/libs/components/generic-crud/generic-crud.component';
import { GenericSearchComponent, IBackendSearchComponent, IBackendSearchComponentConfig } from 'src/app/libs/components/generic-crud/generic-search/generic-search.component';
import { TimeElapsedPipe } from 'src/app/libs/pipes/time-elapsed.pipe';
import { generateGuid } from 'src/app/libs/utils';
import {
  EMPTY_TID, Product, ProductFormat, ProductionOrderPlanner,
  ProductionOrderPlannerOperation, TId
} from 'src/app/models';
import { MasterApiService } from 'src/app/services/master-api.service';

type TFullProduct = Product & { formats: ProductFormat[], quantity: number };

type TView = 'po' | 'operation';  // 'Production order' | 'Operation'

@Component({
    selector: 'app-planner',
    templateUrl: './planner.component.html',
    styleUrl: './planner.component.sass',
    animations: [
        trigger('slideInOut', [
            transition(':enter', [
                style({ transform: 'translateY(100%)' }),
                animate('0.3s ease-in-out', style({ transform: 'translateY(0%)' }))
            ]),
            transition(':leave', [
                animate('0.3s ease-in-out', style({ transform: 'translateY(100%)' }))
            ])
        ]),
    ],
    standalone: false
})
export class PlannerComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('schedulerContainer', { read: ViewContainerRef })
  schedulerContainer!: ViewContainerRef;

  private destroy$: Subject<void>;
  private _ready$: BehaviorSubject<boolean>;
  ready$: Observable<boolean>;
  workCenters: any[];
  events: Partial<any & { desc: string, productionOrderId: TId }>[];
  currentView: TView;
  schedulerViewPreset: string;
  showDetailFullScreen: boolean;
  dependencies: { id: TId, from: TId, to: TId }[];
  private bryntumClassesLoaded = false;
  bryntumAvailable: boolean | undefined = undefined;
  private schedulerComponentRef?: ComponentRef<any>;

  private currentProductionOrders?: ProductionOrderPlanner[];
  selectedProductionOrder?: ProductionOrderPlanner;
  selectedOperationId?: TId;

  searchComponentConfig!: IBackendSearchComponent[];
  searchComponent!: GenericSearchComponent;
  scheduler?: any;
  readonly eventRenderer = (e: { eventRecord: any, renderData: any }): string => {
    if (!StringHelperClass) {
      return `<div class="info"><div class="name">${e.eventRecord.name}</div></div>`;
    }
    return `<div class="info">
  <div class="name">${StringHelperClass.encodeHtml(e.eventRecord.name)}</div>
  <div class="desc">${StringHelperClass.encodeHtml((e.eventRecord as any).desc)}</div>
  ${(this.currentView === 'po') ? '' : ''}
</div>`;
  };

  readonly viewPresets: any[] = [];

  async schedulerInitialized(e: any): Promise<void> {
    this.scheduler = e?.source || e;
    await this.setToday();
  }

  constructor(
    private api: MasterApiService,
    private cdr: ChangeDetectorRef,
    private compiler: Compiler,
    private injector: Injector
  ) {
    this.destroy$ = new Subject<void>();
    this._ready$ = new BehaviorSubject<boolean>(false);
    this.ready$ = this._ready$.asObservable();
    this.events = [];
    this.workCenters = [];
    this.dependencies = [];
    this.currentView = 'po';
    this.showDetailFullScreen = false;
    this.schedulerViewPreset = 'weekAndDay';
    this.loadBryntumClasses();
  }

  private async loadBryntumClasses(): Promise<void> {
    if (!isSchedulerEnabled() || this.bryntumClassesLoaded) {
      console.log('Bryntum Scheduler disabled or already loaded');
      return;
    }
    try {
      console.log('Loading Bryntum Scheduler classes...');
      const classes = await loadSchedulerClasses();
      if (classes) {
        StringHelperClass = classes.StringHelper;
        EventModelClass = classes.EventModel;
        PresetManagerClass = classes.PresetManager;
        ResourceModelConfigClass = classes.ResourceModel;
        SchedulerClass = classes.Scheduler;

        // Configurar viewPresets
        if (PresetManagerClass) {
          this.viewPresets.push(...PresetManagerClass.records.filter((p: any) => [
            'monthAndYear',
            'weekAndMonth',
            'weekAndDay',
            'hourAndDay',
          ].includes(p.id as string)));
        }
        this.bryntumClassesLoaded = true;
        console.log('Bryntum Scheduler classes loaded successfully');
      } else {
        console.warn('Bryntum Scheduler classes returned null');
      }
    } catch (error) {
      console.error('Failed to load Bryntum Scheduler classes', error);
    }
  }

  async ngOnInit(): Promise<void> {
    this.api.auth.user$.pipe(
      takeUntil(this.destroy$),
      filter(user => !!user),
      map(u => u!.lastCompanyId),
      startWith(''),
      distinctUntilChanged((prev, curr) => prev === curr),
    ).subscribe(_companyId => this.initializeData());
  }

  async ngAfterViewInit(): Promise<void> {
    await Promise.resolve();
    setTimeout(() => {
      if (environment.features?.enableScheduler) {
        this.loadBryntumSchedulerComponent();
      } else {
        this.bryntumAvailable = false;
        this.cdr.markForCheck();
      }
    }, 0);
  }

  private async loadBryntumSchedulerComponent(): Promise<void> {
    if (!this.schedulerContainer || this.schedulerComponentRef) {
      return;
    }

    if (!isSchedulerEnabled()) {
      setTimeout(() => {
        this.bryntumAvailable = false;
        this.cdr.markForCheck();
      }, 0);
      return;
    }

    try {
      const factoryResult = await loadSchedulerComponentFactory(
        this.compiler,
        this.injector
      );

      if (!factoryResult) {
        throw new Error('Failed to load Scheduler component factory');
      }

      this.schedulerComponentRef =
        this.schedulerContainer.createComponent(factoryResult.schedulerFactory);

      this.configureSchedulerComponent();
      setTimeout(() => {
        this.bryntumAvailable = true;
        this.cdr.markForCheck();
      }, 0);
    } catch (err) {
      console.error('Failed to load Bryntum Scheduler:', err);
      setTimeout(() => {
        this.bryntumAvailable = false;
        this.cdr.markForCheck();
      }, 0);
    }
  }

  private configureSchedulerComponent(): void {
    if (!this.schedulerComponentRef) {
      return;
    }

    configureSchedulerComponent(
      this.schedulerComponentRef.instance,
      {
        resources: this.workCenters,
        events: this.events,
        dependencies: this.dependencies,
        eventRenderer: this.eventRenderer,
        rowHeight: this.currentView === 'po' ? 60 : 100,
        viewPreset: this.schedulerViewPreset,
        startDate: this.searchComponent?.form?.value?.startDate,
        endDate: this.searchComponent?.form?.value?.endDate,
        columns: [{ field: 'name' }]
      },
      {
        onPaint: (e: any) => this.schedulerInitialized(e),
        onEventResizeEnd: (e: any) => this.updateDates(e),
        onEventDrop: (e: any) => this.updateDates(e),
        onEventClick: (e: any) => this.eventClicked(e),
        onScheduleClick: () => this.emptyClick()
      }
    );
  }

  private updateSchedulerInputs(): void {
    if (!this.schedulerComponentRef) {
      return;
    }

    updateSchedulerComponent(this.schedulerComponentRef.instance, {
      resources: this.workCenters,
      events: this.events,
      dependencies: this.dependencies,
      rowHeight: this.currentView === 'po' ? 60 : 100,
      viewPreset: this.schedulerViewPreset,
      startDate: this.searchComponent?.form?.value?.startDate,
      endDate: this.searchComponent?.form?.value?.endDate
    });
  }

  private async initializeData(): Promise<void> {
    await this.loadBryntumClasses();
    if (!this.bryntumClassesLoaded) {
      console.warn('Bryntum Scheduler is not available');
      this._ready$.next(true);
      return;
    }
    this._ready$.next(false);
    const [
      plants,
    ] = await firstValueFrom(forkJoin([
      this.api.resources.plants.getAll(),
    ]));
    const now = new Date();
    const startDate = new Date();
    const endDate = new Date();
    startDate.setDate(now.getDate() - 1);  // - 1 day
    endDate.setDate(now.getDate() + 15);   // + 15 days
    const api = this.api;
    this.searchComponentConfig = [
      {
        queryParam: 'startDate',
        config: {
          value: startDate,
          label: 'common.startDate',
        },
        type: EBackendFilterTypes.Date,
      }, {
        queryParam: 'endDate',
        config: {
          value: endDate,
          label: 'common.endDate',
        },
        type: EBackendFilterTypes.Date,
      }, {
        queryParam: 'plantId',
        type: EBackendFilterTypes.Selection,
        config: {
          items: plants,
          displayExpr: 'name',
          valueExpr: 'id',
          value: plants.length === 1 ? plants[0].id : undefined,
        }
      }, {
        queryParam: 'simulationId',
        type: EBackendFilterTypes.Selection,
        config: {
          label: 'simulationId',
          items: [],
          displayExpr: 'name',
          valueExpr: 'id',
        },
        active: formChanges => !!formChanges.plantId,
        onFormChange: async formChanges => {
          const simulations = await firstValueFrom(api.resources.operations.simulations.getAll({ plantId: formChanges.plantId }));
          const result: IBackendSearchComponentConfig = {
            items: simulations,
          };
          return result;
        },

      }
    ];
    this._ready$.next(true);
  }

  ngOnDestroy(): void {
    if (this.schedulerComponentRef) {
      this.schedulerComponentRef.destroy();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  async search(e: { plantId?: TId, startDate?: Date, endDate?: Date, simulationId?: TId }): Promise<void> {
    if (!e.plantId || !e.startDate || !e.endDate) {
      return;
    }
    const params = e as { plantId: TId, startDate: Date, endDate: Date, simulationId?: TId };
    switch (this.currentView) {
      case 'po':
        await this.searchOFs(params);
        break;
      case 'operation':
        await this.searchOperations(params);
        break;
    }
  }

  async searchOperations(e: { plantId: TId, startDate: Date, endDate: Date, simulationId?: TId }): Promise<void> {
    const companyId = this.api.auth.userValue!.lastCompanyId;
    const operationsRq = this.api.getPlannerProductionOrders({
      startDate: e.startDate,
      endDate: e.endDate,
      plantId: e.plantId,
      simulationId: e.simulationId,
      companyId,
    }).pipe(
      tap(productionOrders => this.currentProductionOrders = productionOrders),
      switchMap(async productionOrders => {
        const productIds: TId[] = [];
        const operations: ProductionOrderPlannerOperation[] = [];
        productionOrders.forEach(po => {
          const product = po.products.find(p => p.coProductTypeId === 1)!;
          if (!productIds.includes(product.productId)) {
            productIds.push(product.productId);
          }
          po.operations.forEach(o => {
            (o as any).mainProduct = product.productId;  // Temporarily store the main product id instead of the full product
            (o as any).quantity = product.quantity;
            (o as any).poNumber = po.number;
            operations.push(o);
          });
        });
        const productsDict = productIds.length ? await this.fetchProducts(productIds) : {};
        operations.forEach(o => {
          const quantity = (o as any).quantity;
          const mainProduct: TFullProduct = productsDict[(o as any).mainProduct] as TFullProduct;
          mainProduct.quantity = quantity;
          (o as any).mainProduct = mainProduct;
        });
        return operations as (ProductionOrderPlannerOperation & { mainProduct: TFullProduct } & { poNumber: string })[];
      }),
    );

    const plannerWorkcentersRq = this.api.getPlannerWorkCenters(e.plantId).pipe(
      map(wcs => {
        wcs.push({
          id: EMPTY_TID,
          equipments: [],
          code: 'undefined',
          name: 'undefined',
          sectionId: EMPTY_TID,
          stateId: EMPTY_TID,
          productionContainerId: EMPTY_TID,
          preProductionContainerIds: [EMPTY_TID],
          postProductionContainerIds: [EMPTY_TID],
          order: 0,
        });
        wcs.sort((a, b) => a.name.localeCompare(b.name));
        return wcs;
      })
    );

    let operations: (ProductionOrderPlannerOperation & { mainProduct: TFullProduct } & { poNumber: string })[];

    [
      operations,
      this.workCenters,
    ] = await firstValueFrom(forkJoin([
      operationsRq,
      plannerWorkcentersRq,
    ]));

    this.events = [];
    this.dependencies = [];
    const hrt = new TimeElapsedPipe();
    operations.forEach(o => {
      // const mainFormat = o.mainProduct.formats[0];
      // const quantity = o.mainProduct.quantity + ' ' + mainFormat.formatCode + ' - ' + o.mainProduct.code + ' (' + o.mainProduct.name + ')';
      const wcs: Set<string> = new Set<string>();
      operations.forEach(o => o.workCenters.forEach(wc => wcs.add(wc)));
      const po = this.currentProductionOrders!.find(po => po.id === o.productionOrderId);
      let time = '00:00:00';
      po?.operations.filter(op => op.workCenters.some(wc => wcs.has(wc)))
        .forEach(o => time = this.timeAddition(time, o.duration));
      const event: Partial<EventModel & { desc: string, productionOrderId: TId }> = {
        // id: o.id,
        name: o.name,
        productionOrderId: o.productionOrderId,
        draggable: false,
        resizable: false,
        desc: hrt.transform(o.duration),
        startDate: o.startTime,
        endDate: o.endTime,
        eventColor: o.operationTypeMainColor,
      };
      if (o.workCenters.length === 0) {
        o.workCenters.push(EMPTY_TID as string);
      }
      o.workCenters.forEach(wc => {
        const eventId = wc + o.id;
        this.events.push(Object.assign({}, event, {
          id: eventId,
          resourceId: wc,
        }));
        if (o.dependencies.length) {
          o.dependencies.forEach(d => {
            const opTo = operations.find(op => op.id === d.id);
            opTo?.workCenters.forEach(wcTo => {
              this.dependencies.push({
                id: generateGuid(),
                from: eventId,
                to: wcTo + d.id,
              });
            });

          });
        }
      });
    });
    console.log(this.dependencies);
    this.updateSchedulerInputs();
  }

  async searchOFs(e: { plantId: TId, startDate: Date, endDate: Date, simulationId?: TId }): Promise<void> {
    const companyId = this.api.auth.userValue!.lastCompanyId;
    // A record to cache products by id with its formats.
    const productionOrdersRq = this.api.getPlannerProductionOrders({
      startDate: e.startDate,
      endDate: e.endDate,
      plantId: e.plantId,
      simulationId: e.simulationId,
      companyId,
    }).pipe(
      tap(productionOrders => this.currentProductionOrders = productionOrders),
      switchMap(async productionOrders => {
        const productIds: TId[] = [];
        productionOrders.forEach(po => {
          const mainProductId = po.products.find(p => p.coProductTypeId === 1)!.productId;
          (po as any).mainProduct = mainProductId;  // Temporarily store the main product id instead of the full product
          if (!productIds.includes(mainProductId)) {
            productIds.push(mainProductId);
          }
        });

        const productsDict = await this.fetchProducts(productIds);
        productionOrders.forEach(po => {
          const quantity = po.products.find(p => p.productId === (po as any).mainProduct)!.quantity;
          const mainProduct: TFullProduct = productsDict[(po as any).mainProduct] as TFullProduct;
          mainProduct.quantity = quantity;
          (po as any).mainProduct = mainProduct;
        });
        return productionOrders as (ProductionOrderPlanner & { mainProduct: TFullProduct })[];
      })
    );

    const plannerWorkcentersRq = this.api.getPlannerWorkCenters(e.plantId).pipe(
      map(wcs => {
        wcs.push({
          id: EMPTY_TID,
          equipments: [],
          code: 'undefined',
          name: 'undefined',
          sectionId: EMPTY_TID,
          stateId: EMPTY_TID,
          productionContainerId: EMPTY_TID,
          preProductionContainerIds: [EMPTY_TID],
          postProductionContainerIds: [EMPTY_TID],
          order: 0,
        });
        wcs.sort((a, b) => a.name.localeCompare(b.name));
        return wcs;
      })
    );

    let productionOrders: (ProductionOrderPlanner & { mainProduct: TFullProduct })[];

    [
      productionOrders,
      this.workCenters,
    ] = await firstValueFrom(forkJoin([
      productionOrdersRq,
      plannerWorkcentersRq,
    ]));
    this.events = [];
    productionOrders.forEach(po => {
      const wcs: Set<string> = new Set<string>();
      po.operations.forEach(o => o.workCenters.forEach(wc => wcs.add(wc)));
      if (wcs.size === 0) {
        wcs.add(EMPTY_TID as string);
      }
      const mainFormat = po.mainProduct.formats[0];
      const desc = mainFormat.quantity + ' ' + mainFormat.formatCode + ' - ' + po.mainProduct.code + ' (' + po.mainProduct.name + ')';
      const event: Partial<EventModel & { desc: string, productionOrderId: TId }> = {
        name: po.number,
        productionOrderId: po.id,
        desc,
        startDate: po.startDate,
        endDate: po.endDate,
      };
      wcs.forEach(wc => this.events.push(Object.assign({}, event, {
        id: wc + po.id,
        resourceId: wc,
      })));
    });
    this.updateSchedulerInputs();
  }

  async fetchProducts(productIds: TId[]): Promise<Record<TId, TFullProduct>> {
    const productsRqs = productIds.map(pId => this.api.resources.product.products.get(pId).pipe(
      switchMap(async product => {
        const formats = await firstValueFrom(this.api.resources.product.productFormats(product.id).getAll());
        return {
          ...product,
          formats,
        } as TFullProduct;
      }),
    ));

    const products: TFullProduct[] = productIds.length ? await firstValueFrom(forkJoin(productsRqs)) : [];
    const productsDict: Record<TId, TFullProduct> = {};
    products.forEach(p => productsDict[p.id] = p);
    return productsDict;
  }

  async setView(view: TView): Promise<void> {
    this.currentView = view;
    this.updateSchedulerInputs();
    this.search(this.searchComponent.form!.value);
  }

  async updateDates(e: any): Promise<void> {
    if (this.currentView === 'po') {
      // const workCenterId = e.resourceRecord.data.id;
      const eventId = e.resourceRecord.events[0].data.id;
      // const cId = e.assignmentRecord.data.eventId;
      const productionOrderId = eventId.substring(36) ?? null;
      // console.log(e.startDate, e.endDate, workCenterId, productionOrderId);
      let startDate: Date
      let endDate: Date;
      const processSheetId = EMPTY_TID;
      switch (e.eventName) {
        case 'eventDrop':
          startDate = e.context.startDate;

          try {
            await firstValueFrom(this.api.moveProductionOrder(productionOrderId, { startDate, processSheetId }));
          } finally {
            await this.search(this.searchComponent.form!.value);
          }
          break;
        case 'eventResizeEnd':
          startDate = e.startDate;
          endDate = e.endDate;
          try {
            await firstValueFrom(this.api.resizeProductionOrder(productionOrderId, { startDate, endDate }));
          } finally {
            await this.search(this.searchComponent.form!.value);
          }
          break;
      }

    }
  }

  async eventClicked(e: any): Promise<void> {
    const data = e.eventRecord.data;
    // const wcId: TId = data.id.substring(0, 36);
    // const eventId: TId = data.id.substring(36);
    const productionOrderId = data.productionOrderId;
    this.selectedProductionOrder = this.currentProductionOrders!.find(po => po.id === productionOrderId);
    if (this.currentView === 'operation') {
      this.selectedOperationId = data.id.substring(36);
      this.events.forEach(e => {
        e['eventStyle'] = (e['productionOrderId'] === productionOrderId) ? undefined : 'colored';
        // e.eventColor = (e.productionOrderId === productionOrderId) ? 'blue' : undefined;
      });
      this.events = [...this.events];  // Force update reinstancing the array
    }
  }

  async emptyClick(): Promise<void> {
    this.selectedProductionOrder = undefined;
    this.selectedOperationId = undefined;
    this.events.forEach(e => e['eventStyle'] = undefined);
    this.events = [...this.events];  // Force update reinstancing the array
  }

  async setToday(): Promise<void> {
    this.scheduler?.scrollToDate(new Date());
  }

  onGenericSearchInitialized(e: GenericSearchComponent): void {
    this.searchComponent = e;
    this.api.auth.user$.pipe(
      takeUntil(this.destroy$),
      filter(user => !!user),
      map(user => user!.lastCompanyId),
    ).subscribe(async () => {
      await this.search(this.searchComponent.form!.value);
      this.updateSchedulerInputs();
    });
  }

  /**
   * Add time to a time string
   * @param time format 'HH:mm:ss'
   * @param addition format 'HH:mm:ss'
   * @returns format 'HH:mm:ss'
   */
  private timeAddition(time: string, addition: string): string {
    // time and addition to seconds
    const timeParts = time.split(':');
    const timeSeconds = (+timeParts[0]) * 3600 + (+timeParts[1]) * 60 + (+timeParts[2]);
    const additionParts = addition.split(':');
    const additionSeconds = (+additionParts[0]) * 3600 + (+additionParts[1]) * 60 + (+additionParts[2]);
    // sum
    const resultSeconds = timeSeconds + additionSeconds;
    // result to time
    const hours = Math.floor(resultSeconds / 3600);
    const minutes = Math.floor((resultSeconds % 3600) / 60);
    const seconds = resultSeconds % 60;
    // result must be in format 'HH:mm:ss' (2 digits for each part)
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
}
